import {useCallback, useEffect, useState} from 'react';
import usePayinSessionQuery from 'src/api/graphql/usePayinSessionQuery';
import {useSessionContext} from 'src/state/usePayinSessionContext';
import {isMobile} from 'src/utils/utils';
import {notifyInContextComplete} from 'src/v2/util/inContextCompletion';
import {urlRedirect} from '@stripe-internal/safe-links';
import {getStore} from 'src/v2/state/store';
import {getLogger} from 'src/utils/logger';

const POLL_INTERVAL_MS = 2000;

const logger = getLogger();

export const useRelayPaymentPolling = (): {
  startPolling: () => void;
} => {
  const store = getStore();
  const [isPolling, setIsPolling] = useState(false);
  const {clientSecret, sessionId} = useSessionContext();
  const returnUrl = store.useSelector(
    (state) => state.paymentDetails.returnUrl,
  );

  const payinSessionData = usePayinSessionQuery({
    variables: {
      input: {
        crypto_payin_session: sessionId,
        client_secret: clientSecret,
      },
    },
    skip: !isPolling || !sessionId || !clientSecret,
    pollInterval: POLL_INTERVAL_MS,
    component: 'useRelayPaymentPolling',
  });

  const autoRedirectDisabled = isMobile();

  const startPolling = useCallback(() => {
    setIsPolling(true);
  }, []);

  useEffect(() => {
    const payinSession = payinSessionData?.data?.v1CryptoInternalPayinSession;

    if (isPolling && payinSession) {
      const sessionState = payinSession.state;
      const relayStatus = payinSession.max_relay_request_status ?? null;

      store.setValue((state) => {
        state.state = payinSession.state;
        state.transactionDetails.blockchainTxId =
          payinSession.transaction_details?.blockchain_tx_id ?? null;
        state.anyCryptoDeposit.maxRelayRequestStatus = relayStatus;

        if (
          state.anyCryptoDeposit.step === 'deposit' &&
          relayStatus !== null &&
          relayStatus !== 'waiting'
        ) {
          state.anyCryptoDeposit.step = 'processing';
        }
      });

      if (sessionState === 'fulfillment_complete') {
        setIsPolling(false);
        if (!notifyInContextComplete() && !autoRedirectDisabled) {
          logger.log('crypto_payin_session.screen.rendered', {
            screen: 'SUCCESS',
            message: 'redirect for relay payin session',
          });
          urlRedirect(returnUrl);
        }
      } else if (sessionState === 'error' || sessionState === 'canceled') {
        setIsPolling(false);
        store.setValue((state) => {
          state.screen = sessionState === 'error' ? 'ERROR' : 'CANCELED';
        });
      }
    }
  }, [isPolling, payinSessionData, autoRedirectDisabled, store, returnUrl]);

  return {
    startPolling,
  };
};
